import React, { memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { MenuItem, Select, Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { mcRejectionReasonTitles } from 'src/constants/document-review/constants'
import { McRejectionReason } from 'src/constants/document-review/enums'

interface Props {
  isOpen: boolean
  isSubmitting: boolean
  onConfirm(declineReason: string): void
  onClose(): void
}

/**
 * There is no "Other" option here, unlike the contractor recollect modal — the MC rejection
 * reasons are a closed static list (`McRejectionReason`) and the backend DTO has no free-text
 * field beyond `declineReason` itself.
 */
const RejectMcDocumentModalComponent = ({ isOpen, isSubmitting, onConfirm, onClose }: Props) => {
  const [declineReason, setDeclineReason] = useState<string>('')

  const isConfirmDisabled = declineReason === '' || isSubmitting

  const handleConfirm = useCallback(() => {
    onConfirm(declineReason)
  }, [onConfirm, declineReason])

  return (
    <CommonModal
      isOpen={isOpen}
      title="Reject this document?"
      text="The master contractor is notified and asked to resubmit."
      onClose={onClose}
      buttons={
        <div css={tw`flex flex-row gap-2`}>
          <Button color="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            data-testid="reject-confirm-button"
          >
            Reject
          </Button>
        </div>
      }
    >
      <div css={tw`flex flex-col gap-1`}>
        <Typography variant="body2">Reason</Typography>
        <Select
          value={declineReason}
          onChange={(event) => {
            setDeclineReason(String(event.target.value))
          }}
          displayEmpty
          data-testid="reject-reason-select"
        >
          <MenuItem value="" disabled>
            Select a reason…
          </MenuItem>
          {Object.values(McRejectionReason).map((reason) => {
            return (
              <MenuItem key={reason} value={reason}>
                {mcRejectionReasonTitles[reason]}
              </MenuItem>
            )
          })}
        </Select>
      </div>
    </CommonModal>
  )
}

export const RejectMcDocumentModal = memo(RejectMcDocumentModalComponent)
